import { SourcingStats } from 'api-clients/monolith';
import { format } from 'date-fns';
import { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import ExclamationCircle from '@/assets/icons/exclamation-circle.svg';
import { Typography, TypographyVariant } from '@/components/ui/typography';
import { useHireUrl } from '@/hooks/use-hire-url';
import { OTHER } from '@/types/channels';
import { getProgress } from '@/utils/progress';

import { Button } from './button';
import { Progress } from './progress';
import { Tooltip } from './tooltip';

interface DurationProps {
  stats: SourcingStats;
  channelSlug?: string;
  brand?: string;
  location?: string;
  funnel?: string;
  showMetricsPeriod?: boolean;
  /** When no hiring goal: null = lifetime (org setting), number = last N days, undefined = not loaded (treat as 30). */
  metricsPeriod?: number | null;
  showProgress?: boolean;
  typographyVariant?: TypographyVariant;
  showDays?: boolean;
  showDateRange?: boolean;
  /** The range shown in place of days; a Date names a calendar day as read in the browser. */
  startDate?: string | Date;
  endDate?: string | Date;
  tooltipContent?: ReactNode;
  showNewGoalButton?: boolean;
  showDurationWarning?: boolean;
  onAddButtonClick?: () => void;
  hiringGoalAutomationEnabled?: boolean;
  showOnlyActual?: boolean;
}

export function Duration({
  stats,
  channelSlug,
  brand,
  location,
  funnel,
  showMetricsPeriod,
  metricsPeriod,
  showProgress = false,
  typographyVariant = 'text',
  showDays = true,
  showDateRange = false,
  startDate,
  endDate,
  tooltipContent,
  showNewGoalButton = false,
  showDurationWarning = false,
  onAddButtonClick,
  hiringGoalAutomationEnabled,
  showOnlyActual = false,
}: DurationProps) {
  const { getHireATHiringGoalUrl } = useHireUrl();
  let { expected, actual } = stats;
  if (actual && expected && actual > expected) {
    actual = expected;
  }
  const progress = getProgress(expected, actual);
  const intl = useIntl();

  function NewGoalLinkButton({
    customText,
  }: {
    customText?: ReactNode;
  } = {}) {
    return (
      <Button variant="link" asChild>
        <Link
          to={getHireATHiringGoalUrl(brand ?? 'no_brand', location, funnel)}
          target="_blank"
        >
          {customText ?? (
            <FormattedMessage
              defaultMessage="+ New goal"
              description="Link beside duration when user should add a hire-by goal in Hire"
            />
          )}
        </Link>
      </Button>
    );
  }

  if (showNewGoalButton) return <NewGoalLinkButton />;

  if (showMetricsPeriod) {
    if (metricsPeriod === null) {
      return (
        <FormattedMessage
          defaultMessage="Lifetime"
          description="Duration: all-time window when org default metrics period is lifetime (no goal)"
        />
      );
    }
    const days = metricsPeriod ?? 30;
    return (
      <FormattedMessage
        defaultMessage="Last {days} days"
        values={{ days }}
        description="Duration: rolling window when no hiring goal is set; {days} is the org default period"
      />
    );
  }

  const renderContent = () => {
    if (showOnlyActual) {
      return `${actual ?? '-'}`;
    }
    if (showDateRange && startDate && endDate) {
      return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d')}`;
    }

    if (!expected && !actual) return <>- / -</>;

    if (actual && !expected)
      return (
        <>
          {actual}
          <span> / </span>
          {onAddButtonClick ? (
            <Button variant="link" onClick={onAddButtonClick}>
              <FormattedMessage
                defaultMessage="+ Add"
                description="Link to add an end date to the hiring goal"
              />
            </Button>
          ) : channelSlug === OTHER ? (
            <span>-</span>
          ) : (
            <NewGoalLinkButton
              customText={
                <FormattedMessage
                  defaultMessage="+ Add"
                  description="Link to add an end date to the hiring goal"
                />
              }
            />
          )}
        </>
      );

    if (showDays) {
      const daysLeft =
        expected != null && actual != null ? expected - actual : null;
      const shownWarning = !hiringGoalAutomationEnabled && showDurationWarning;

      return (
        <>
          <div className="flex h-[16px] w-full items-center gap-1">
            <FormattedMessage
              defaultMessage="{actual} / {expected} days"
              values={{ actual: actual ?? '-', expected: expected ?? '-' }}
              description="Elapse vs target window: {actual} and {expected} are day counts"
            />
            {shownWarning && (
              <Tooltip
                content={intl.formatMessage(
                  {
                    defaultMessage:
                      'The hiring goal for this opening ends in {daysLeft} {daysWord}. Consider extending the end date of your goal or make changes to your campaigns reach your goal.',
                    description:
                      'The hiring goal for this opening ends in {daysLeft} {daysWord}. Consider extending the end date of your goal or make changes to your campaigns reach your goal.',
                  },
                  {
                    daysLeft,
                    daysWord: daysLeft === 1 ? 'day' : 'days',
                  },
                )}
              >
                <img
                  src={ExclamationCircle}
                  alt="Ends in 1 day"
                  className="ml-auto h-[16px] w-auto"
                />
              </Tooltip>
            )}
          </div>
        </>
      );
    }

    return `${actual ?? '-'} / ${expected ?? '-'}`;
  };

  return (
    <>
      <Tooltip content={tooltipContent} className="w-90">
        <Typography variant={typographyVariant}>{renderContent()}</Typography>
      </Tooltip>
      {!showOnlyActual && showProgress && progress > 0 && (
        <Progress value={progress} />
      )}
    </>
  );
}
